/**
 * URL STATE (B10) — pure parse / sanitize / serialize for deep links. Tested in urlState.test.ts.
 *
 *   ?place=<place id>   the selected place
 *   ?cat=<category id>  the pressed chip (restores category + bloom)
 *   ?mood=golden|night  the map light (day is the default and is left out)
 *   ?kiosk=1            read-only: layout switch, never written or removed
 *
 * Every other parameter (?qa, ?pr, ?light, ?harness …) is preserved as is.
 */
import type { Dataset } from "@/contracts/data";
import type { Mood } from "@/contracts/store";

export const DEFAULT_MOOD: Mood = "day";
const MOODS: readonly Mood[] = ["day", "golden", "night"];
export const isMood = (v: unknown): v is Mood => typeof v === "string" && (MOODS as readonly string[]).includes(v);

/** What the URL carries. null = absent. */
export interface UrlState {
  place: string | null;
  cat: string | null;
  mood: Mood | null;
}

/** Raw read (no validation against the data). */
export function parseUrlState(search: string): UrlState {
  const q = new URLSearchParams(search);
  const s = (k: string) => {
    const v = q.get(k);
    return v && v.trim() ? v.trim() : null;
  };
  const mood = s("mood");
  return { place: s("place"), cat: s("cat"), mood: isMood(mood) ? mood : null };
}

/**
 * Drops ids the dataset doesn't know. The site rows are not selectable: the site is "home".
 * A chip that disagrees with the place (?place=airport&cat=education) is dropped:
 * the place wins, because selecting it moves the panel to its own group.
 */
export function sanitizeUrlState(u: UrlState, data: Pick<Dataset, "places" | "categories"> | null): UrlState {
  if (!data) return { place: null, cat: null, mood: u.mood };
  const place = u.place ? data.places.find((p) => p.id === u.place && !p.isSite) : undefined;
  const cat = u.cat ? data.categories.find((c) => c.id === u.cat) : undefined;
  const catOk = cat && (!place || !place.category || place.category === cat.id);
  return { place: place ? place.id : null, cat: catOk ? cat.id : null, mood: u.mood };
}

/** The URL's view of the store. */
export function urlStateOf(s: { selectedId: string | null; category: string | null; mood: Mood }): UrlState {
  return { place: s.selectedId, cat: s.category, mood: s.mood };
}

/**
 * New `search` string ("" or "?…") for a state, keeping every unrelated
 * parameter and its position. Default mood (day) is left out.
 */
export function serializeUrlState(search: string, u: UrlState): string {
  const q = new URLSearchParams(search);
  const put = (k: string, v: string | null) => {
    if (v) q.set(k, v);
    else q.delete(k);
  };
  put("place", u.place);
  put("cat", u.cat);
  put("mood", u.mood && u.mood !== DEFAULT_MOOD ? u.mood : null);
  const out = q.toString();
  return out ? `?${out}` : "";
}

/** Same meaning, whatever the parameter order or encoding. */
export function sameSearch(a: string, b: string): boolean {
  const norm = (s: string) => {
    const q = new URLSearchParams(s);
    q.sort();
    return q.toString();
  };
  return norm(a) === norm(b);
}

/**
 * push when the selection changed (so Back returns to the previous place);
 * replace for everything else (chip, mood). null = nothing to write.
 */
export function historyMode(
  prev: { selectedId: string | null },
  next: { selectedId: string | null; selectSource?: string | null },
  currentSearch: string,
  nextSearch: string,
): "push" | "replace" | null {
  if (sameSearch(currentSearch, nextSearch)) return null;
  if (prev.selectedId !== next.selectedId && next.selectSource !== "attract") return "push";
  return "replace";
}
